// Champions Academy news — extracted and translated from the official yearly
// "News Page" decks (2018–2023). Each article maps to one story; photos live
// under public/img/news/<slug>/NN.jpg.

export interface NewsItem {
  id: string;
  slug: string;
  title: {
    en: string;
    ar: string;
  };
  excerpt: {
    en: string;
    ar: string;
  };
  content: {
    en: string;
    ar: string;
  };
  /** Cover image (first photo of the gallery). */
  image: string;
  /** All photos for the article, in order. */
  gallery: string[];
  date: string;
  category: string;
  author: string;
}

/** Bilingual labels for the category keys used below. */
export const CATEGORY_LABELS: Record<string, { en: string; ar: string }> = {
  achievements: { en: 'Achievements', ar: 'إنجازات' },
  partnerships: { en: 'Partnerships', ar: 'شراكات' },
  events: { en: 'Events', ar: 'فعاليات' },
  international: { en: 'International', ar: 'دولي' },
  facilities: { en: 'Facilities', ar: 'مرافق' },
  community: { en: 'Community', ar: 'مجتمع' },
};

/** Build an ordered gallery path list for a slug with `count` photos. */
const gallery = (slug: string, count: number): string[] =>
  Array.from(
    { length: count },
    (_, i) => `/img/news/${slug}/${String(i + 1).padStart(2, '0')}.jpg`,
  );

const cover = (slug: string): string => `/img/news/${slug}/01.jpg`;

export const news: NewsItem[] = [
  // ===================== 2023 =====================
  {
    id: '2023-gaza-clubs-championship',
    slug: 'gaza-clubs-championship',
    title: {
      en: "Champions Win the Gaza Clubs & Academies Championship",
      ar: 'أبطال تشامبيونز يتوجون ببطولة الأندية والأكاديميات على مستوى قطاع غزة',
    },
    excerpt: {
      en: 'Champions claim the cubs-category title across Gaza and dedicate it to their late teammate Rayan Muharib.',
      ar: 'أبطالنا يحصدون لقب فئة الأشبال على مستوى القطاع ويهدون البطولة لروح زميلهم الراحل ريان محارب.',
    },
    content: {
      en: `Champions were crowned title-holders of the Clubs & Academies Championship across the Gaza Strip in the cubs category (born 2008 and above), after a deserved win over Ittihad Khan Younis on penalties in a thrilling, closely contested final that stayed tense until the final moments.

The young champions took to the podium with pride, securing the fourth star of their journey and reaffirming Champions' standing as one of the most prominent clubs and academies on the local sporting scene.

The tournament carried a moving human dimension: the players honored their pledge to dedicate the title to the soul of their late teammate, champion Rayan Muharib. Immediately after the match, they headed to the family's home to honor them and hand over the trophy — a gesture of loyalty that embodies the one-family spirit that distinguishes Champions.

A new achievement added to the trophy record, reflecting the continuous work and determination that drive Champions' athletes toward ever more brilliance and success.`,
      ar: `تُوّج أبطال تشامبيونز بلقب بطولة الأندية والأكاديميات على مستوى قطاع غزة لفئة الأشبال – مواليد 2008 فما فوق، بعد فوز مستحق على نادي اتحاد خانيونس بركلات الترجيح، في مواجهة اتسمت بالإثارة والندية حتى اللحظات الأخيرة.

واعتلى الأبطال منصة التتويج بكل فخر واعتزاز، محققين النجمة الرابعة في مسيرتهم، ومؤكدين مكانة تشامبيونز كأحد أبرز الأندية والأكاديميات على الساحة الرياضية في القطاع.

وكان للبطولة بُعد إنساني مؤثر، حيث أوفى اللاعبون بعهدهم بإهداء اللقب لروح زميلهم الراحل، البطل ريان محارب. وعقب المباراة مباشرة، توجه الأبطال إلى منزل الفقيد لتكريم عائلته وتسليمهم اللقب، في لفتة وفاء تجسد روح الأسرة الواحدة التي تميز تشامبيونز.

إنجاز جديد يُضاف إلى سجل البطولات، ويعكس العمل المتواصل والإصرار الذي يقود أبطال تشامبيونز نحو المزيد من التألق والنجاح.`,
    },
    image: cover('gaza-clubs-championship'),
    gallery: gallery('gaza-clubs-championship', 5),
    date: '2023-11-20',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2023-karate-age-categories',
    slug: 'karate-age-categories-2023',
    title: {
      en: 'Champions Karate Athletes Take Top Places in the Age-Categories Tournament',
      ar: 'أبطال تشامبيونز يحصدون المراكز المتقدمة في بطولة الفئات العمرية للكاراتيه',
    },
    excerpt: {
      en: 'Strong showings in both Kata and Kumite earn Champions several podium finishes at the Palestinian Karate Federation event.',
      ar: 'أداء قوي في الكاتا والكوميتيه يمنح أبطالنا عدة مراكز متقدمة في بطولة الاتحاد الفلسطيني للكاراتيه.',
    },
    content: {
      en: `Champions Academy's karate athletes capped their participation in the age-categories tournament — organized by the Palestinian Karate Federation across the Kata and Kumite events — with a number of top finishes, following a strong performance that reflected thorough technical preparation and high readiness.

The results were as follows:
• Saeed Shamlakh — 2nd place (Kumite)
• Aseel Bisharat — 3rd place (Kata)
• Ahmad Eslim — 3rd place (Kumite)
• Talia Rashid — 5th place (Kata and Kumite)

This achievement confirms the continued development of the academy's athletes and the success of a training program that prepares champions capable of competing strongly across the various local fixtures.

Congratulations to our champions on this distinction, with our warmest wishes for more successes in the tournaments ahead.`,
      ar: `كلّل أبطال أكاديمية تشامبيونز مشاركتهم في بطولة الفئات العمرية، التي نظمها الاتحاد الفلسطيني للكاراتيه ضمن منافسات الكاتا والكوميتيه، بتحقيق عدد من المراكز المتقدمة، بعد أداء قوي عكس حجم التحضيرات الفنية والجاهزية العالية.

وجاءت نتائج الأبطال على النحو التالي:
• سعيد شملخ – المركز الثاني (كوميتيه)
• أسيل بشارات – المركز الثالث (كاتا)
• أحمد إسليم – المركز الثالث (كوميتيه)
• تاليا رشيد – المركز الخامس (كاتا وكوميتيه)

ويؤكد هذا الإنجاز استمرار تطور مستوى لاعبي الأكاديمية، ونجاح البرنامج التدريبي في إعداد أبطال قادرين على المنافسة بقوة في مختلف الاستحقاقات المحلية.

مبارك لأبطالنا هذا التميز، مع أطيب التمنيات لهم بمزيد من النجاحات في البطولات القادمة.`,
    },
    image: cover('karate-age-categories-2023'),
    gallery: gallery('karate-age-categories-2023', 6),
    date: '2023-10-15',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2023-table-tennis-honoring',
    slug: 'table-tennis-honoring',
    title: {
      en: 'Champions Honor Their Table-Tennis Title-Winners',
      ar: 'تتويج أبطال تشامبيونز في بطولة الفئات العمرية لتنس الطاولة',
    },
    excerpt: {
      en: 'The academy celebrates its table-tennis medalists with players, technical and administrative staff in attendance.',
      ar: 'الأكاديمية تحتفل بأبطالها المتصدرين لبطولة الفئات العمرية لتنس الطاولة بحضور اللاعبين والطواقم.',
    },
    content: {
      en: `Champions Academy celebrated its athletes who topped the table-tennis age-categories tournament, in a ceremony attended by the academy's players and its administrative and technical staff, following their first-place medals in the competition.

The recognition came in appreciation of the great effort the players put in throughout the season, and as a crowning of a continuous string of achievements at home and abroad that has strengthened the academy's position as one of the leading platforms for producing champions in table tennis.

This celebration underscores Champions' keenness to entrench a culture of appreciation and motivation, and to support its athletes' journey toward further excellence and distinction in the fixtures ahead.`,
      ar: `احتفلت أكاديمية تشامبيونز بأبطالها المتصدرين لبطولة الفئات العمرية لتنس الطاولة، وذلك بمشاركة لاعبي الأكاديمية والطواقم الإدارية والفنية، عقب تحقيقهم عددًا من ميداليات المراكز الأولى في البطولة.

وجاء هذا التكريم تقديرًا للجهود الكبيرة التي بذلها اللاعبون طوال الموسم، وتتويجًا لسلسلة من الإنجازات المتواصلة على الصعيدين المحلي والدولي، والتي عززت مكانة الأكاديمية كإحدى أبرز منصات صناعة الأبطال في رياضة تنس الطاولة.

ويؤكد هذا الاحتفاء حرص تشامبيونز على ترسيخ ثقافة التقدير والتحفيز، ودعم مسيرة لاعبيها نحو المزيد من التفوق والتميز في الاستحقاقات القادمة.`,
    },
    image: cover('table-tennis-honoring'),
    gallery: gallery('table-tennis-honoring', 4),
    date: '2023-09-25',
    category: 'events',
    author: 'Champions Academy',
  },
  {
    id: '2023-table-tennis-age-categories',
    slug: 'table-tennis-age-categories-2023',
    title: {
      en: 'Champions Sweep the Table-Tennis Age-Categories Medals',
      ar: 'أبطال فلسطين من تشامبيونز يحصدون الميداليات المتقدمة في بطولة الفئات العمرية لتنس الطاولة',
    },
    excerpt: {
      en: 'Across five age groups, Champions take the most podium places — including the youngest player honored for beating opponents from the U-19 bracket.',
      ar: 'عبر خمس فئات عمرية يحصد أبطالنا أكبر عدد من المراكز المتقدمة، وتكريم أصغر لاعب بعد فوزه على لاعبين من فئة 19 عامًا.',
    },
    content: {
      en: `Champions Academy's table-tennis athletes continued their national-level brilliance, completing the age-categories tournament organized by the Palestinian Table Tennis Federation across five different age groups and securing the largest number of advanced placings thanks to outstanding performances and quality physical preparation.

This achievement crowns the academy's ongoing commitment to providing a model sporting environment that has honed the players' skills and raised their competitive readiness — clearly reflected in the honorable results delivered at the close of the tournament.

The tournament also saw player Khaled Bdeir honored as the youngest participant, after a remarkable performance and a win over players in the under-19 category — an achievement that reflects the talent and confidence Champions' athletes possess.

The medal tally was as follows:
• International champion Abdullah Mushtaha — Gold, U-15
• Khaled Bdeir — Gold, U-11
• Maher Al-Rayyes — Silver, U-11
• Yousef Asfour — Bronze, U-13
• Hasan Abu Shaaban — Bronze, U-15
• International champion Abdullah Mushtaha — Bronze, U-17
• International champion Abdullah Mushtaha — Bronze, U-19
• International champion Yousef Mushtaha — Bronze, U-19

A new achievement that cements Champions Academy's standing as one of Palestine's leading table-tennis schools, and confirms the continued journey of producing champions and leading at every level.`,
      ar: `واصل أبطال أكاديمية تشامبيونز لتنس الطاولة تألقهم على مستوى الوطن، بعدما استكملوا منافسات بطولة الفئات العمرية التي نظمها الاتحاد الفلسطيني لتنس الطاولة، ضمن خمس فئات عمرية مختلفة، محققين أكبر عدد من المراكز المتقدمة بفضل الأداء المميز والإعداد البدني النوعي.

ويأتي هذا الإنجاز تتويجًا للاهتمام المتواصل الذي توليه الأكاديمية لتوفير بيئة رياضية نموذجية، أسهمت في صقل مهارات اللاعبين ورفع جاهزيتهم التنافسية، ما انعكس بوضوح على النتائج المشرفة التي تحققت في ختام البطولة.

وشهدت البطولة تكريم اللاعب خالد بدير بصفته أصغر لاعب مشارك، بعد أن قدّم أداءً لافتًا وحقق الفوز على لاعبين من فئة 19 عامًا، في إنجاز يعكس حجم الموهبة والثقة التي يتمتع بها أبطال تشامبيونز.

وجاءت حصيلة الميداليات على النحو الآتي:
• البطل الدولي عبد الله مشتهى – ذهبية فئة 15 عامًا
• البطل خالد بدير – ذهبية فئة 11 عامًا
• البطل ماهر الريس – فضية فئة 11 عامًا
• البطل يوسف عصفور – برونزية فئة 13 عامًا
• البطل حسن أبو شعبان – برونزية فئة 15 عامًا
• البطل الدولي عبد الله مشتهى – برونزية فئة 17 عامًا
• البطل الدولي عبد الله مشتهى – برونزية فئة 19 عامًا
• البطل الدولي يوسف مشتهى – برونزية فئة 19 عامًا

إنجاز جديد يرسّخ مكانة أكاديمية تشامبيونز كإحدى أبرز مدارس تنس الطاولة في فلسطين، ويؤكد استمرار مسيرة صناعة الأبطال وتحقيق الريادة على مختلف المستويات.`,
    },
    image: cover('table-tennis-age-categories-2023'),
    gallery: gallery('table-tennis-age-categories-2023', 4),
    date: '2023-08-18',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2023-real-madrid-camp-jerusalem',
    slug: 'real-madrid-camp-jerusalem',
    title: {
      en: 'Champions Players Complete the Second Real Madrid Camp in Jerusalem',
      ar: 'لاعبو تشامبيونز يختتمون مشاركتهم في معسكر ريال مدريد بالقدس',
    },
    excerpt: {
      en: 'Champions athletes wrap up the second Real Madrid training camp with a certified coaching certificate from the Spanish club.',
      ar: 'أبطالنا يختتمون معسكر ريال مدريد التدريبي الثاني بالحصول على شهادة معتمدة من النادي الملكي.',
    },
    content: {
      en: `Champions players concluded their distinguished participation in the second Real Madrid training camp, held in the other part of the homeland, after a rich experience full of knowledge and quality skills.

The camp marked an important milestone in developing the players' technical and physical abilities, allowing them to engage with coaches certified by the Royal Club and to train according to a modern European methodology that broadened their footballing horizons and deepened their professional understanding of the game.

The champions wrapped up their participation by obtaining a coaching certificate accredited by Real Madrid — a valuable addition to their sporting record and a boost to their future journey toward higher levels of development and professionalism.

This participation falls within Champions' vision of empowering its players to undertake quality external experiences that help refine their skills and cement their presence on the sporting scene.`,
      ar: `أنهى لاعبو تشامبيونز مشاركتهم المميزة في معسكر ريال مدريد التدريبي الثاني، الذي أُقيم في الشق الآخر من الوطن، بعد تجربة غنية بالمعرفة والمهارات النوعية.

وشكّل المعسكر محطة مهمة في تطوير قدرات اللاعبين الفنية والبدنية، حيث أتاح لهم الاحتكاك بمدربين معتمدين من النادي الملكي، والتدرب وفق منهجية أوروبية حديثة أسهمت في توسيع آفاقهم الكروية وتعزيز فهمهم الاحترافي للعبة.

واختتم الأبطال مشاركتهم بالحصول على شهادة تدريبية معتمدة من ريال مدريد، تمثل إضافة قيمة إلى سجلهم الرياضي، ودعمًا لمسيرتهم المستقبلية نحو مستويات أعلى من التطور والاحتراف.

وتأتي هذه المشاركة ضمن رؤية تشامبيونز لتمكين لاعبيها من خوض تجارب خارجية نوعية تسهم في صقل مهاراتهم وترسيخ حضورهم في المشهد الرياضي.`,
    },
    image: cover('real-madrid-camp-jerusalem'),
    gallery: gallery('real-madrid-camp-jerusalem', 5),
    date: '2023-07-10',
    category: 'partnerships',
    author: 'Champions Academy',
  },

  // ===================== 2022 =====================
  {
    id: '2022-real-madrid-camp-graduation',
    slug: 'real-madrid-camp-graduation',
    title: {
      en: 'Champions Celebrate the Graduation of the First Real Madrid Camp in Gaza',
      ar: 'تشامبيونز تحتفل بتخريج المشاركين في معسكر ريال مدريد التدريبي الأول في غزة',
    },
    excerpt: {
      en: 'Players receive their camp-completion certificates alongside Spanish coach David Hernández in a festive ceremony.',
      ar: 'تسليم اللاعبين شهادات ختام المعسكر بحضور المدرب الإسباني ديفيد هيرنانديز في أجواء احتفالية.',
    },
    content: {
      en: `At Champions, we always strive to be the best and strongest in sport, and to graduate champions capable of representing Palestine in international arenas at the hands of world-class experts.

Champions celebrated the graduation of the athletes who took part in the first Real Madrid training camp in the Gaza Strip, in the presence of the players and the Spanish coach David Hernández, along with a host of notable sporting figures. The players were handed their camp-completion certificates amid the encouragement of their families, in a festive atmosphere of joy and pride — making the camp a step that continues their professional dream.

Alongside this, the academy's management honored coach David Hernández for his efforts and dedication to the players and the Champions staff, as well as the coach's interpreter Dr. Salah Al-Sousi for his efforts in accompanying the Spanish coach and the Champions teams who contributed to the camp's success.`,
      ar: `نسعى في تشامبيونز لنكون دائمًا الأفضل والأقوى في مجال الرياضة وتخريج أبطال قادرين على المشاركة في تمثيل فلسطين في المحافل الدولية على يد خبراء دوليين في الرياضة.

احتفلت تشامبيونز بتخريج الأبطال المشاركين في معسكر ريال مدريد التدريبي الأول في قطاع غزة بحضور اللاعبين والمدرب الإسباني ديفيد هيرنانديز وبوجود لفيف من الشخصيات الاعتبارية الرياضية، حيث تم تسليم اللاعبين شهادات ختام المعسكر التدريبي بتشجيع من أهالي اللاعبين بأجواء احتفالية وفرحة بأبنائهم، ليكون المعسكر هو خطة يُستكمل بها حلمهم الاحترافي.

وبجانب ذلك كرّمت إدارة الأكاديمية المدرب ديفيد هيرنانديز على مجهوداته وعطائه الذي قدمه للاعبين وطواقم تشامبيونز، ومترجم المدرب الدكتور صلاح السوسي على مجهوداته ومرافقته للمدرب الإسباني وطواقم تشامبيونز المشاركة في نجاح المعسكر التدريبي.`,
    },
    image: cover('real-madrid-camp-graduation'),
    gallery: gallery('real-madrid-camp-graduation', 6),
    date: '2022-12-15',
    category: 'events',
    author: 'Champions Academy',
  },
  {
    id: '2022-real-madrid-camp-gaza',
    slug: 'real-madrid-camp-gaza',
    title: {
      en: 'Real Madrid in Gaza: The First Training Camp Led by a Royal-Club Coach Begins',
      ar: 'ريال مدريد في غزة… انطلاق أول معسكر تدريبي بإشراف مدرب من النادي الملكي',
    },
    excerpt: {
      en: 'Real Madrid coach David Hernández arrives in Gaza for a month-long camp — a historic first for Palestinian youth football.',
      ar: 'وصول مدرب ريال مدريد ديفيد هيرنانديز إلى غزة لإطلاق أول معسكر تدريبي للناشئين الفلسطينيين لمدة شهر.',
    },
    content: {
      en: `In a historic, first-of-its-kind event, Spanish Real Madrid coach David Hernández arrived in the Gaza Strip on Friday morning from the capital Madrid for a month-long visit to launch the first training camp of its kind for Palestinian youth.

At a press conference held at its Gaza headquarters, Champions Academy announced the start of the camp, which aims to transfer European football expertise through an integrated training program covering technical-skill development, physical conditioning and capacity building — putting the players at the start of the road to professional sport.

Rajab Al-Sarraj, chairman of the Champions Group board, said: "This is a dream we worked on for seven years; despite postponements for several reasons, we held on to it until it came true," affirming that the step represents a new launch toward the global stage for Palestinian talents.

For his part, the Madridista coach David Hernández expressed his happiness with the experience, praising the warm reception, and said: "This is a new experience for Real Madrid and for me personally. We are here not only to teach football, but also to learn and to convey the wonderful image we see in Gaza."

Gaza Mayor Yahya Al-Sarraj described the event as an achievement for Palestine in general and the Gaza Strip in particular, considering it a prelude to coming successes. Abdul Salam Haniyeh, assistant secretary-general of the Higher Council for Youth and Sports, thanked Champions Academy for the initiative that brought Real Madrid together with Palestine, while Palestinian Football Association member Yasser Radwan affirmed the federation's readiness to support everything that develops Palestinian football and serves the southern governorates.

In the main sponsor's word, Jawwal's commercial director in Gaza, Abdel Fattah Al-Sharafa, affirmed that the company's support stems from its social responsibility toward the sports sector; the event was also sponsored by Bank of Palestine (Al-Quds Bank) and the Rami Al-Daboos company.

Mahmoud Sharaf, vice-chairman of the Champions board, closed the conference by officially announcing the launch of the first Real Madrid training camp in the Gaza Strip, affirming that Champions' pitches would witness, in the period ahead, Palestinian training with a European flavor.

Champions Academy had signed a partnership agreement with Real Madrid Academy in early 2020 within the framework of sporting exchange; this camp comes as one of the fruits of that agreement and a practical step toward strengthening international cooperation in developing Palestinian football.`,
      ar: `في حدث تاريخي هو الأول من نوعه، وصل صباح يوم الجمعة إلى قطاع غزة مدرب ريال مدريد الإسباني ديفيد هيرنانديز، قادمًا من العاصمة مدريد، في زيارة تمتد لشهر كامل، لإطلاق أول معسكر تدريبي من نوعه للناشئين الفلسطينيين.

وأعلنت أكاديمية تشامبيونز، خلال مؤتمر صحفي عُقد في مقرها بمدينة غزة، عن بدء فعاليات المعسكر الذي يهدف إلى نقل الخبرات الأوروبية في كرة القدم، عبر برنامج تدريبي متكامل يشمل تطوير المهارات الفنية، والتهيئة البدنية، وبناء القدرات، بما يسهم في وضع اللاعبين على بداية طريق الاحتراف الرياضي.

وقال رجب السراج، رئيس مجلس إدارة مجموعة تشامبيونز: "هذا حلم عملنا عليه لسبع سنوات، ورغم التأجيل لعدة ظروف، إلا أننا تمسكنا به حتى تحقق"، مؤكدًا أن هذه الخطوة تمثل انطلاقة جديدة نحو العالمية للمواهب الفلسطينية.

من جهته، أعرب المدرب المدريدي ديفيد هيرنانديز عن سعادته بهذه التجربة، مشيدًا بحفاوة الاستقبال، وقال: "هذه تجربة جديدة لريال مدريد ولي شخصيًا، نحن هنا ليس فقط لتعليم كرة القدم، بل لنتعلم أيضًا وننقل الصورة الرائعة التي نراها في غزة".

بدوره، وصف يحيى السراج رئيس بلدية غزة الحدث بأنه إنجاز لفلسطين عامة ولقطاع غزة بشكل خاص، معتبرًا إياه مقدمة لنجاحات قادمة. كما توجه الأمين العام المساعد للمجلس الأعلى للشباب والرياضة عبد السلام هنية بالشكر لأكاديمية تشامبيونز على هذه المبادرة التي جمعت ريال مدريد بفلسطين، فيما أكد عضو الاتحاد الفلسطيني لكرة القدم ياسر رضوان جاهزية الاتحاد لدعم كل ما من شأنه تطوير الكرة الفلسطينية وخدمة المحافظات الجنوبية.

وفي كلمة الراعي الرئيسي للحدث، أكد المدير التجاري لشركة جوال في غزة عبد الفتاح الشرفا أن دعم الشركة يأتي انطلاقًا من مسؤوليتها المجتمعية تجاه القطاع الرياضي، فيما يرعى الحدث أيضًا كل من بنك القدس وشركة رامي الضابوس.

واختتم محمود شرف، نائب رئيس مجلس إدارة تشامبيونز، المؤتمر بالإعلان رسميًا عن انطلاق فعاليات المعسكر التدريبي الأول لريال مدريد في قطاع غزة، مؤكدًا أن ملاعب تشامبيونز ستشهد خلال الفترة المقبلة تدريبات فلسطينية بنكهة أوروبية.

يُذكر أن أكاديمية تشامبيونز كانت قد وقعت اتفاقية شراكة مع أكاديمية ريال مدريد مطلع عام 2020 في إطار التبادل الرياضي، ويأتي هذا المعسكر كإحدى ثمار تلك الاتفاقية وخطوة عملية نحو تعزيز التعاون الدولي في تطوير كرة القدم الفلسطينية.`,
    },
    image: cover('real-madrid-camp-gaza'),
    gallery: gallery('real-madrid-camp-gaza', 4),
    date: '2022-11-18',
    category: 'partnerships',
    author: 'Champions Academy',
  },
  {
    id: '2022-egypt-training-camp',
    slug: 'egypt-training-camp',
    title: {
      en: "Champions' Table-Tennis Squad Begins a Training Camp in Egypt",
      ar: 'انطلاق معسكر تدريبي لأبطال تشامبيونز في تنس الطاولة بجمهورية مصر العربية',
    },
    excerpt: {
      en: 'Champions table-tennis athletes open an intensive camp in Egypt under Egyptian Table Tennis Federation experts.',
      ar: 'بعثة تشامبيونز لتنس الطاولة تنطلق في معسكر مكثف بمصر تحت إشراف خبراء الاتحاد المصري.',
    },
    content: {
      en: `The training camp for the Champions table-tennis delegation got under way in the Arab Republic of Egypt, as part of the "living-experience" and training-camp program aimed at developing the players' technical and physical levels.

The first day featured fitness training and warm-ups before moving into intensive sessions on table-tennis skills, within an integrated program focused on raising physical readiness and enhancing technical performance.

The training is held under the supervision and follow-up of fitness trainer Islam Al-Sheikh, and Captain Khaled Hassan — the delegation's technical director and the person in charge of the Egyptian national-champion project — alongside Captain Hisham Abu Hashish, a member of the Egyptian Table Tennis Federation and one of the game's prominent experts.

This camp comes within Champions' vision of providing quality external exposure for its players and acquiring advanced training experience that helps prepare champions capable of competing strongly in the upcoming fixtures.`,
      ar: `انطلقت فعاليات المعسكر التدريبي الخاص ببعثة أبطال تشامبيونز لتنس الطاولة في جمهورية مصر العربية، ضمن برنامج المعايشات والمعسكرات التدريبية الهادفة إلى تطوير المستوى الفني والبدني للاعبين.

وشهد اليوم الأول تنفيذ تدريبات اللياقة البدنية والإحماء، قبل الدخول في حصص تدريبية مكثفة على مهارات تنس الطاولة، في إطار برنامج متكامل يركز على رفع الجاهزية البدنية وتعزيز الأداء الفني.

وتُقام التدريبات تحت إشراف ومتابعة المعد البدني إسلام الشيخ، والكابتن خالد حسن المدير الفني للبعثة والمسؤول عن مشروع البطل القومي المصري، إلى جانب الكابتن هشام أبو حشيش، عضو الاتحاد المصري لتنس الطاولة وأحد الخبراء البارزين في اللعبة.

ويأتي هذا المعسكر ضمن رؤية تشامبيونز لتوفير احتكاك خارجي نوعي للاعبيها، واكتساب خبرات تدريبية متقدمة تسهم في إعداد أبطال قادرين على المنافسة بقوة في الاستحقاقات القادمة.`,
    },
    image: cover('egypt-training-camp'),
    gallery: gallery('egypt-training-camp', 7),
    date: '2022-10-12',
    category: 'events',
    author: 'Champions Academy',
  },
  {
    id: '2022-yousef-aljamal-spain-loan',
    slug: 'yousef-aljamal-spain-loan',
    title: {
      en: 'Champions Loan Yousef Al-Jamal to a Spanish Academy in a Historic Move',
      ar: 'تشامبيونز يعلن إعارة يوسف الجمل إلى أكاديمية إسبانية في حدث تاريخي',
    },
    excerpt: {
      en: 'Champions youngster Yousef Al-Jamal becomes the first Palestinian youth footballer loaned to a Spanish academy with a buy option.',
      ar: 'الناشئ يوسف الجمل يصبح أول لاعب فلسطيني يُعار إلى أكاديمية إسبانية مع بند أحقية الشراء.',
    },
    content: {
      en: `In an unprecedented, historic moment for Palestinian and Arab sport, Champions Club announced the loan of its young player Yousef Al-Jamal to one of the Spanish academies, with the agreement including a buy-option clause — making him the first Palestinian youth footballer to undertake this professional experience in such an official manner.

The announcement came in an enthusiastic atmosphere dominated by feelings of pride and joy, attended by board members and the technical and administrative staff, alongside a large number of academy players who shared this exceptional moment — a new achievement added to the Champions journey.

This step reflects the success of the club's strategic vision in opening external pathways to professionalism for Palestinian talents, and confirms its ability to create real opportunities for players to reach European levels within an ambitious, professional framework.`,
      ar: `في لحظة تاريخية غير مسبوقة على صعيد الرياضة الفلسطينية والعربية، أعلن نادي تشامبيونز عن إعارة لاعبه الناشئ يوسف الجمل إلى إحدى الأكاديميات الإسبانية، مع تضمين الاتفاقية بند أحقية الشراء، ليصبح أول ناشئ فلسطيني في كرة القدم يخوض هذه التجربة الاحترافية بهذا الشكل الرسمي.

وجاء الإعلان في أجواء حماسية غلبت عليها مشاعر الفخر والفرح، بحضور أعضاء مجلس الإدارة والطواقم الفنية والإدارية، إلى جانب عدد كبير من لاعبي الأكاديميات، الذين شاركوا هذه اللحظة الاستثنائية التي تُعد إنجازًا جديدًا يُضاف إلى مسيرة تشامبيونز.

وتعكس هذه الخطوة نجاح الرؤية الاستراتيجية للنادي في فتح مسارات الاحتراف الخارجي أمام المواهب الفلسطينية، وتأكيد قدرته على صناعة فرص حقيقية للاعبين للوصول إلى المستويات الأوروبية، ضمن منظومة عمل احترافية وطموحة.`,
    },
    image: cover('yousef-aljamal-spain-loan'),
    gallery: gallery('yousef-aljamal-spain-loan', 3),
    date: '2022-09-05',
    category: 'international',
    author: 'Champions Academy',
  },
  {
    id: '2022-table-tennis-division-three',
    slug: 'table-tennis-division-three',
    title: {
      en: 'Champions Win Division Three and Earn Promotion in Table Tennis',
      ar: 'تشامبيونز يتوج ببطولة الدوري العام لتنس الطاولة – الدرجة الثالثة ويصعد للدرجة الثانية',
    },
    excerpt: {
      en: 'Champions table-tennis team tops the third-division general league and books promotion to the second division.',
      ar: 'فريق تنس الطاولة يحصد المركز الأول في الدرجة الثالثة ويصعد رسميًا إلى الدرجة الثانية.',
    },
    content: {
      en: `In a scene reflecting a spirit of determination and ambition, the Champions table-tennis team was crowned first in the third-division general table-tennis league, officially booking their place in the second division after a distinguished performance that confirmed their readiness and deserved promotion.

Champions' players dazzled everyone with their high energy and competitive spirit, embodying the true meaning of drive toward achieving goals and making accomplishments — a new station added to the academy's record of successes.

This title reflects the organized work and developmental vision that Champions Academy pursues in preparing players capable of competing and rising steadily toward higher levels.

Congratulations to our champions on this well-deserved achievement, with our sincere wishes for more success and brilliance in all the sporting fixtures ahead.`,
      ar: `في مشهد يعكس روح الإصرار والطموح، توّج أبطال فريق تشامبيونز لتنس الطاولة بالمركز الأول في الدوري العام لتنس الطاولة – الدرجة الثالثة، ليحجزوا مقعدهم رسميًا في دوري الدرجة الثانية، بعد أداء مميز أكد جاهزيتهم واستحقاقهم للصعود.

وأبهر لاعبو تشامبيونز الجميع بطاقاتهم العالية وروحهم التنافسية، مجسدين معنى الحافز الحقيقي نحو تحقيق الأهداف وصناعة الإنجازات، في محطة جديدة تضاف إلى سجل نجاحات الأكاديمية.

ويعكس هذا التتويج العمل المنظم والرؤية التطويرية التي تنتهجها أكاديمية تشامبيونز في إعداد لاعبين قادرين على المنافسة والصعود بثبات نحو مستويات أعلى.

مبارك لأبطالنا هذا الإنجاز المستحق، مع خالص التمنيات لهم بمزيد من التوفيق والتألق في كافة المحافل الرياضية القادمة.`,
    },
    image: cover('table-tennis-division-three'),
    gallery: gallery('table-tennis-division-three', 6),
    date: '2022-07-20',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2022-table-tennis-age-categories',
    slug: 'table-tennis-age-categories-2022',
    title: {
      en: 'Champions Take the Most Podium Places in the Table-Tennis Age-Categories Tournament',
      ar: 'تشامبيونز يحصد أكبر عدد من المراكز المتقدمة في بطولة الفئات العمرية لتنس الطاولة',
    },
    excerpt: {
      en: 'Across five age groups, Champions secure the largest number of advanced placings in table tennis.',
      ar: 'عبر خمس فئات عمرية يحصد فريق تنس الطاولة أكبر عدد من المراكز المتقدمة.',
    },
    content: {
      en: `The Champions table-tennis team continued its strong presence, securing the largest number of advanced placings in the age-categories tournament organized by the Palestinian Table Tennis Federation for the 2003–2004, 2005–2006, 2007–2008, 2009–2010 and 2011-and-under categories.

The results of Champions' athletes were as follows:
• Abdullah Mushtaha — 1st place (born 2009)
• Yousef Mushtaha — 2nd place (born 2005)
• Yousef Mushtaha — 2nd place (born 2003)
• Khaled Bdeir — 3rd place (born 2011)
• Badr Mushtaha — 3rd place, repeated (born 2011)
• Hasan Abu Shaaban — 3rd place (born 2007)
• Hasan Abu Shaaban — 3rd place (born 2005)
• Yasser Mushtaha — 3rd place (born 2005)

This achievement confirms the superiority of Champions Academy and its continued preparation of players capable of competing strongly across the various age categories, within a professional training system built on continuous development and the making of champions.

Congratulations to our champions on this renewed brilliance, with our warmest wishes for more leadership and success in every sporting arena.`,
      ar: `واصل فريق تشامبيونز لتنس الطاولة حضوره القوي، بعدما حصد أكبر عدد من المراكز المتقدمة في بطولة الفئات العمرية التي نظمها الاتحاد الفلسطيني لتنس الطاولة، لفئات (2003–2004)، (2005–2006)، (2007–2008)، (2009–2010)، و(2011 فما دون).

وجاءت نتائج أبطال تشامبيونز على النحو التالي:
• البطل عبد الله مشتهى – المركز الأول (فئة 2009)
• البطل يوسف مشتهى – المركز الثاني (فئة 2005)
• البطل يوسف مشتهى – المركز الثاني (فئة 2003)
• البطل خالد بدير – المركز الثالث (فئة 2011)
• البطل بدر مشتهى – المركز الثالث مكرر (فئة 2011)
• البطل حسن أبو شعبان – المركز الثالث (فئة 2007)
• البطل حسن أبو شعبان – المركز الثالث (فئة 2005)
• البطل ياسر مشتهى – المركز الثالث (فئة 2005)

ويؤكد هذا الإنجاز تفوق أكاديمية تشامبيونز واستمرارها في إعداد لاعبين قادرين على المنافسة بقوة في مختلف الفئات العمرية، ضمن منظومة تدريبية احترافية ترتكز على التطوير المستمر وصناعة الأبطال.

مبارك لأبطالنا هذا التألق المتجدد، مع أطيب التمنيات لهم بمزيد من الصدارة والنجاح في كافة المحافل الرياضية.`,
    },
    image: cover('table-tennis-age-categories-2022'),
    gallery: gallery('table-tennis-age-categories-2022', 3),
    date: '2022-06-15',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2022-karate-komite-individual',
    slug: 'karate-komite-individual',
    title: {
      en: 'Champions Shine in Their First Official Karate Kumite Tournament',
      ar: 'أبطال تشامبيونز يتألقون في أول مشاركة رسمية ببطولة "كوميتيه" الفردي',
    },
    excerpt: {
      en: 'In their first official competitive appearance, Champions karate athletes win two golds, two silvers and a bronze.',
      ar: 'في أول ظهور رسمي يحصد أبطال الكاراتيه ذهبيتين وفضيتين وبرونزية.',
    },
    content: {
      en: `Champions Academy's karate athletes recorded a distinguished participation in the individual "Kumite" age-categories tournament organized by the Palestinian Karate Federation — their first official competitive appearance — delivering remarkable performances that reflected their technical readiness and high competitive spirit.

The participation produced striking results:
• Zainab Jasim Al-Shawa — Gold medal
• Ahmad Salim — Gold medal
• Tia Al-Rayyes — Silver medal
• Saeed Shamlakh — Silver medal
• Riyad Al-Zahri — Bronze medal

The tournament also saw an honorable showing from players Mohammed Al-Atrash, Yahya Al-Dahdouh and Karim Al-Heles, who delivered strong performances reflecting the advanced technical level within the academy.

These results confirm the success of Champions' karate training program and form an important step within the players' competitive agenda, paving the way for more local and external fixtures. All the best to our Champions athletes in their coming journey, after a distinguished display in their first official participation.`,
      ar: `سجل أبطال أكاديمية الكاراتيه في تشامبيونز مشاركة مميزة في بطولة الفئات العمرية "كوميتيه" الفردي، التي نظمها الاتحاد الفلسطيني للكاراتيه، وذلك في أول ظهور رسمي لهم على الصعيد التنافسي، مقدمين مستويات مميزة عكست جاهزيتهم الفنية وروحهم التنافسية العالية.

وأسفرت المشاركة عن تحقيق نتائج لافتة:
• اللاعبة زينب جاسم الشوا – الميدالية الذهبية
• اللاعب أحمد سليم – الميدالية الذهبية
• اللاعبة تيا الريس – الميدالية الفضية
• اللاعب سعيد شملخ – الميدالية الفضية
• اللاعب رياض الزهري – الميدالية البرونزية

كما شهدت البطولة مشاركة مشرفة من اللاعبين محمد الأطرش، ويحيى الدحدوح، وكريم الهليس، الذين قدموا أداءً قويًا عكس المستوى الفني المتطور داخل الأكاديمية.

وتؤكد هذه النتائج نجاح البرنامج التدريبي لأكاديمية تشامبيونز للكاراتيه، وتشكل خطوة مهمة ضمن أجندة المشاركات التنافسية للاعبين، تمهيدًا لمزيد من الاستحقاقات المحلية والخارجية. كل التوفيق لأبطالنا التشامبيونيين في مسيرتهم القادمة، بعد أداء مميز في أولى مشاركاتهم الرسمية.`,
    },
    image: cover('karate-komite-individual'),
    gallery: gallery('karate-komite-individual', 7),
    date: '2022-05-10',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2022-nafs-sports-partnership',
    slug: 'nafs-sports-partnership',
    title: {
      en: 'A Sporting Partnership Between Champions and Nafs Sports',
      ar: 'شراكة رياضية بين تشامبيونز وشركة نافس للرياضات والمعايشات الرياضية',
    },
    excerpt: {
      en: 'Champions sign a partnership with Nafs Sports to develop professional pathways and quality training programs.',
      ar: 'تشامبيونز توقع شراكة مع شركة نافس لتطوير مسارات الاحتراف والبرامج التدريبية.',
    },
    content: {
      en: `As part of its strategic plan to expand and strengthen Arab partnerships, Champions Academy announced the signing of a sporting partnership with Nafs Sports & Sports Living-Experiences Company — a step aimed at developing professional pathways and elevating the training programs offered to players.

The partnership centers on organizing sporting living-experiences, holding specialized training camps and participating in tournaments, alongside opening Arab professional horizons for sporting talents — helping refine players' skills and give them diverse competitive experience.

This agreement comes within Champions' vision of building an effective regional cooperation network that strengthens its sporting presence and provides quality opportunities for players and coaches alike.`,
      ar: `ضمن خطتها الاستراتيجية للتوسع وتعزيز الشراكات العربية، أعلنت أكاديمية تشامبيونز عن توقيع شراكة رياضية مع شركة نافس للرياضات والمعايشات الرياضية، في خطوة تهدف إلى تطوير مسارات الاحتراف والارتقاء بالبرامج التدريبية المقدمة للاعبين.

وتتمحور الشراكة حول تنظيم المعايشات الرياضية، وإقامة المعسكرات التدريبية المتخصصة، والمشاركة في البطولات، إلى جانب فتح آفاق الاحتراف العربي أمام المواهب الرياضية، بما يسهم في صقل مهارات اللاعبين وإكسابهم خبرات تنافسية متنوعة.

وتأتي هذه الاتفاقية في إطار رؤية تشامبيونز لبناء شبكة تعاون إقليمي فاعلة، تعزز حضورها الرياضي وتوفر فرصًا نوعية للاعبين والمدربين على حد سواء.`,
    },
    image: cover('nafs-sports-partnership'),
    gallery: gallery('nafs-sports-partnership', 2),
    date: '2022-03-08',
    category: 'partnerships',
    author: 'Champions Academy',
  },

  // ===================== 2021 =====================
  {
    id: '2021-table-tennis-federation-cup',
    slug: 'table-tennis-federation-cup',
    title: {
      en: 'Champions Athletes Take Top Places in the Table-Tennis Federation Tournament',
      ar: 'أبطال تشامبيونز يحصدون المراكز المتقدمة في بطولة الاتحاد الفلسطيني لتنس الطاولة',
    },
    excerpt: {
      en: 'Champions table-tennis players secure a series of advanced placings across all age categories.',
      ar: 'لاعبو تنس الطاولة يحققون سلسلة من المراكز المتقدمة لكافة الفئات العمرية.',
    },
    content: {
      en: `Champions Academy's table-tennis athletes continued their brilliance, achieving a series of advanced placings in the Palestinian Table Tennis Federation tournament across all age categories — a new achievement added to the academy's rich record.

The results were as follows:
• Abdullah Mushtaha — 1st place (born 2008)
• Yasser Mushtaha — 2nd place (born 2006)
• Yousef Asfour — 3rd place (born 2010)
• Yousef Medhat Mushtaha — 2nd place (born 2002)
• Bassam Suhail Al-Madani — 3rd place (born 2002)

All of Champions' participating athletes were also honored in appreciation of their distinguished performance and high competitive spirit across the various matches, along with the honoring of the Champions table-tennis coaches led by Captain Asaad Erheem, by the Palestinian Table Tennis Federation.

This achievement reflects the scale of the organized technical work within the academy and its continued preparation of champions capable of competing strongly across the different age categories.`,
      ar: `واصل أبطال أكاديمية تشامبيونز لتنس الطاولة تألقهم، بعدما حققوا سلسلة من المراكز المتقدمة في بطولة الاتحاد الفلسطيني لتنس الطاولة لكافة الفئات العمرية، في إنجاز جديد يُضاف إلى سجل الأكاديمية الحافل.

وجاءت النتائج على النحو التالي:
• البطل عبدالله مشتهى – المركز الأول (فئة 2008)
• البطل ياسر مشتهى – المركز الثاني (فئة 2006)
• اللاعب يوسف عصفور – المركز الثالث (فئة 2010)
• اللاعب يوسف مدحت مشتهى – المركز الثاني (فئة 2002)
• البطل بسام سهيل المدني – المركز الثالث (فئة 2002)

كما تم تكريم جميع أبطال تشامبيونز المشاركين في البطولة، تقديرًا لأدائهم المميز وروحهم التنافسية العالية في مختلف المباريات، إلى جانب تكريم مدربي أكاديمية تشامبيونز لتنس الطاولة بقيادة الكابتن أسعد ارحيم، من قبل الاتحاد الفلسطيني لكرة الطاولة.

ويعكس هذا الإنجاز حجم العمل الفني المنظم داخل الأكاديمية، واستمرارها في إعداد أبطال قادرين على المنافسة بقوة في مختلف الفئات العمرية.`,
    },
    image: cover('table-tennis-federation-cup'),
    gallery: gallery('table-tennis-federation-cup', 4),
    date: '2021-11-22',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2021-first-karate-tournament',
    slug: 'first-karate-tournament',
    title: {
      en: 'Champions Make Their First Official Karate Appearance',
      ar: 'أول مشاركة رسمية لأبطال تشامبيونز في بطولة الكاراتيه',
    },
    excerpt: {
      en: 'Champions Karate Academy debuts at the Sheikh Jarrah Kata championship, with Mohammed Al-Atrash claiming bronze.',
      ar: 'أكاديمية الكاراتيه تسجل حضورها الأول في بطولة الشيخ جراح للكاتا واللاعب محمد الأطرش يحصد البرونزية.',
    },
    content: {
      en: `Champions Karate Academy recorded its first appearance in an official tournament, taking part in the Sheikh Jarrah Kata championship organized by the Palestinian Karate Federation for colored belts in the 2008, 2011, 2012 and 2013 age categories.

The tournament saw the brilliance of player Mohammed Al-Atrash, who was crowned with the bronze medal — an achievement that marks the academy's first success at the level of official participation.

Also taking part were:
• Ashraf Al-Turk (2008 category)
• Tim Al-Madhoun (2011 category)
• Tia Al-Rayyes (girls category)

Champions' players delivered a strong, honorable performance that reflected the level of preparation and technical development within the academy, confirming their readiness to compete in the local arenas.

This participation confirms that the journey of achievements continues, and that Champions' athletes are moving with steady steps toward more successes in the tournaments to come.`,
      ar: `سجلت أكاديمية تشامبيونز للكاراتيه حضورها الأول في بطولة رسمية، من خلال المشاركة في بطولة الشيخ جراح للكاتا، التي نظمها الاتحاد الفلسطيني للكاراتيه للأحزمة الملونة للفئات العمرية 2008 و2011 و2012 و2013.

وشهدت البطولة تألق اللاعب محمد الأطرش الذي توّج بالميدالية البرونزية، في إنجاز يُعد باكورة نجاحات الأكاديمية على مستوى المشاركات الرسمية.

كما شارك في البطولة كل من:
• أشرف الترك (فئة 2008)
• تيم المدهون (فئة 2011)
• تيا الريس (فئة البنات)

وقدم لاعبو تشامبيونز أداءً قويًا ومشرفًا عكس مستوى التحضير والتطور الفني داخل الأكاديمية، مؤكدين جاهزيتهم للمنافسة في المحافل المحلية.

وتؤكد هذه المشاركة أن مسيرة الإنجازات مستمرة، وأن أبطال تشامبيونز يسيرون بخطى ثابتة نحو المزيد من النجاحات في قادم البطولات.`,
    },
    image: cover('first-karate-tournament'),
    gallery: gallery('first-karate-tournament', 4),
    date: '2021-09-18',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2021-alkarnz-2008-football-title',
    slug: 'alkarnz-2008-football-title',
    title: {
      en: 'Champions Win the Late Abdullah Al-Karnz Tournament (2008 Category)',
      ar: 'تشامبيونز يتوج ببطولة الراحل عبد الله الكرنز لفئة 2008',
    },
    excerpt: {
      en: 'Champions lift the 2008-category title on penalties at Palestine International Stadium.',
      ar: 'أبطالنا يحصدون لقب فئة 2008 بركلات الترجيح على أرضية ستاد فلسطين الدولي.',
    },
    content: {
      en: `The Champions team was crowned champion of the late Abdullah Al-Karnz tournament for the 2008 category, held on the pitch of Palestine International Stadium amid a competitive atmosphere and a distinguished crowd.

The title came after a strong encounter against Al-Mohtarfeen (Professionals) Academy, which ended in regulation time in a goalless draw (0–0) before penalties settled the match in favor of the Champions athletes (5–3), placing the team on the podium fully deserving the title.

The tournament was held under the supervision of the Higher Council for Youth and Sports and the Palestinian Football Association in the Gaza Strip, and witnessed a wide attendance of parents and academy players — a scene reflecting the great support for the youth journey and the strengthening of the spirit of sporting competition.

This achievement confirms Champions' continued consolidation of its place among the most prominent football academies in the Gaza Strip, and its ongoing harvest of titles across the various age categories.`,
      ar: `تُوِّج فريق تشامبيونز بلقب بطولة الراحل عبد الله الكرنز لفئة 2008، والتي أُقيمت على أرضية ستاد فلسطين الدولي، وسط أجواء تنافسية وحضور جماهيري مميز.

وجاء التتويج عقب مواجهة قوية أمام أكاديمية المحترفين، انتهى وقتها الأصلي بالتعادل السلبي (0-0)، قبل أن تحسم ركلات الترجيح اللقاء لصالح أبطال تشامبيونز بنتيجة (5-3)، ليعتلي الفريق منصة التتويج عن جدارة واستحقاق.

وأُقيمت البطولة بإشراف المجلس الأعلى للشباب والرياضة والاتحاد الفلسطيني لكرة القدم في قطاع غزة، وشهدت حضورًا واسعًا من أولياء الأمور ولاعبي الأكاديمية، في مشهد عكس الدعم الكبير لمسيرة الناشئين وتعزيز روح المنافسة الرياضية.

ويؤكد هذا الإنجاز استمرار تشامبيونز في ترسيخ مكانته بين أبرز أكاديميات كرة القدم في قطاع غزة، ومواصلة حصد البطولات في مختلف الفئات العمرية.`,
    },
    image: cover('alkarnz-2008-football-title'),
    gallery: gallery('alkarnz-2008-football-title', 4),
    date: '2021-05-14',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2021-girls-basketball-3x3-title',
    slug: 'girls-basketball-3x3-title',
    title: {
      en: 'Champions Girls Win the 3×3 Basketball Title for the Second Year Running',
      ar: 'تشامبيونز تحصد المركز الأول للمرة الثانية تواليًا في بطولة الاتحاد الفلسطيني لكرة السلة 3×3 للفتيات',
    },
    excerpt: {
      en: "Champions' girls basketball team retains the Women's-Day U-16 3×3 title in the southern governorates.",
      ar: 'بطلات تشامبيونز يحتفظن بلقب بطولة 3×3 للفتيات بمناسبة يوم المرأة لفئة أقل من 16 عامًا.',
    },
    content: {
      en: `Champions Academy's basketball girls continued their brilliance, taking first place for the second year in a row in the Palestinian Basketball Federation's 3×3 girls tournament in the southern governorates, held to mark Women's Day in the under-16 category.

This achievement confirmed the strong, sustained presence of the Champions athletes and earned them a back-to-back title in an exceptional sporting season, after a distinguished performance and a high competitive spirit that reflected the scale of work and commitment within the academy.

Champions were represented in the tournament by:
• Champion Salma Salouha
• Champion Noor Abu Jabal
• Champion Lina Abu Shaaban
• Champion Basma Abu Al-Naja

This title embodies the fruits of the strategic vision of Champions Basketball Academy, which is built on developing players according to professional training foundations and building generations capable of competing at high levels that ensure continuity in winning titles.

All congratulations to our champions, with our best wishes for more success and brilliance in their sporting journey.`,
      ar: `واصلت بطلات أكاديمية تشامبيونز لكرة السلة تألقهن، بعدما تُوّجن بالمركز الأول للمرة الثانية على التوالي في بطولة الاتحاد الفلسطيني لكرة السلة (3×3) للفتيات بالمحافظات الجنوبية، والتي أُقيمت بمناسبة يوم المرأة لفئة أقل من 16 عامًا.

وجاء هذا الإنجاز ليؤكد الحضور القوي والمستمر لبطلات تشامبيونز، ويمنحهن لقب الثنائية في موسم رياضي استثنائي، بعد أداء مميز وروح تنافسية عالية عكست حجم العمل والالتزام داخل الأكاديمية.

ومثّل تشامبيونز في البطولة كل من:
• البطلة سلمى صلوحة
• البطلة نور أبو جبل
• البطلة لينا أبو شعبان
• البطلة بسمة أبو النجا

ويجسد هذا التتويج ثمار الرؤية الاستراتيجية لأكاديمية تشامبيونز لكرة السلة، والتي ترتكز على تطوير اللاعبات وفق أسس تدريبية احترافية، وبناء أجيال قادرة على المنافسة بمستويات عالية تضمن الاستمرارية في حصد البطولات.

كل التهنئة لبطلاتنا، مع أطيب التمنيات لهن بمزيد من النجاح والتألق في مسيرتهن الرياضية.`,
    },
    image: cover('girls-basketball-3x3-title'),
    gallery: gallery('girls-basketball-3x3-title', 4),
    date: '2021-03-08',
    category: 'achievements',
    author: 'Champions Academy',
  },

  // ===================== 2020 =====================
  {
    id: '2020-gaza-academies-cup-gold',
    slug: 'gaza-academies-cup-gold',
    title: {
      en: 'Champions Sit Atop the Gaza Academies Cup and Take Double Gold',
      ar: 'تشامبيونز يتربع على عرش بطولة كأس أكاديميات غزة ويحصد الذهب',
    },
    excerpt: {
      en: 'Champions win both the 2006/2007 and 2008/2009 finals to claim the Gaza Academies Cup.',
      ar: 'أبطالنا يتوجون بلقبي فئتي 2006/2007 و2008/2009 في كأس أكاديميات غزة.',
    },
    content: {
      en: `Champions Academy's athletes continued their footballing brilliance, being crowned with both titles of the Gaza Academies Cup organized by the Palestinian Football Association in Gaza, confirming their strong presence and dominance on the podiums.

The titles came after Champions' victory in the finals of the 2006/2007 and 2008/2009 categories over the Professionals team, in encounters marked by strength and rivalry, with our champions claiming both titles fully and deservedly.

This achievement reflects the continuous work the academy puts into developing talents and honing players' abilities according to professional training foundations, cementing its place as one of the most prominent football academies in the Gaza Strip.

Congratulations to the Champions athletes on this double achievement — always rising to the challenge and raising the club's name high.`,
      ar: `واصل أبطال أكاديمية تشامبيونز تألقهم الكروي، بعدما تُوّجوا بلقبي بطولة كأس أكاديميات غزة التي نظمها الاتحاد الفلسطيني لكرة القدم في غزة، مؤكدين حضورهم القوي وهيمنتهم على منصات التتويج.

وجاء التتويج عقب فوز تشامبيونز في نهائي فئتي 2006/2007 و2008/2009 على فريق المحترفين، في مواجهات اتسمت بالقوة والندية، ليحصد أبطالنا اللقبين عن جدارة واستحقاق.

ويعكس هذا الإنجاز العمل المتواصل الذي تبذله الأكاديمية في تطوير المواهب وصقل قدرات اللاعبين وفق أسس تدريبية احترافية، لترسيخ مكانتها كإحدى أبرز الأكاديميات الكروية في قطاع غزة.

هنيئًا لأبطال تشامبيونز هذا الإنجاز المزدوج، ودائمًا على قدر التحدي رافعين اسم النادي عاليًا.`,
    },
    image: cover('gaza-academies-cup-gold'),
    gallery: gallery('gaza-academies-cup-gold', 4),
    date: '2020-12-10',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2020-real-madrid-partnership',
    slug: 'real-madrid-partnership',
    title: {
      en: 'Champions Partner with the Real Madrid Foundation — "Real Madrid in Gaza"',
      ar: 'شراكة تشامبيونز مع مؤسسة ريال مدريد… "ريال مدريد في غزة"',
    },
    excerpt: {
      en: 'Champions activate their Real Madrid partnership with a month-long specialized football camp led by Royal-Club coaches.',
      ar: 'تشامبيونز تفعّل شراكتها مع ريال مدريد عبر معسكر تدريبي متخصص لمدة شهر بإشراف مدربي النادي الملكي.',
    },
    content: {
      en: `Champions Academy announced the activation of the first steps of the partnership it concluded last year with Spain's Real Madrid, through a specialized football training camp supervised by coaches from the Royal Club — their first official visit to the Gaza Strip.

The camp, which extends for a full month, aims to transfer European expertise to Palestinian youth through an integrated training program covering technical-skill development, physical conditioning and capacity building — placing players at the start of the road to footballing professionalism on modern, scientific foundations.

For his part, Champions Academy board chairman Rajab Al-Sarraj expressed his hope that the camp would form a true launching point for Palestinian talents toward global horizons, affirming that Palestine is rich with promising sporting energies that need support and the right conditions to be brought out. He added that the academy is working on quality international projects that will bring about a strategic shift in football.

The academy opened registration for the 6-to-16 age groups, within specific numbers matching the camp's capacity, with participants divided into groups by age in preparation for the start of the training program. A number of Champions' technical staff also took part alongside the Real Madrid coaches, to exchange expertise and strengthen local coaching competence.

Participating players would receive priority to take part in the youth tournaments Real Madrid organizes in the future, in addition to a certificate accredited by the Royal Club upon successful completion of the program.

Champions Academy is considered one of the largest sports academies in Palestine and the Arab world, and has achieved a qualitative leap in developing youth sports in the Gaza Strip through international partnerships and agreements and active participation in local and international tournaments, alongside its crowning with the Gaza Academies Cup across the various age categories.`,
      ar: `أعلنت أكاديمية تشامبيونز عن تفعيل أولى خطوات الشراكة التي أبرمتها العام الماضي مع نادي ريال مدريد الإسباني، من خلال تنظيم معسكر تدريبي متخصص في كرة القدم، بإشراف مدربين من النادي الملكي، في أول زيارة رسمية لهم إلى قطاع غزة.

ويهدف المعسكر، الذي يمتد لمدة شهر كامل، إلى نقل الخبرات الأوروبية إلى الناشئين الفلسطينيين عبر برنامج تدريبي متكامل يشمل تطوير المهارات الفنية، والتهيئة البدنية، وبناء القدرات، بما يضع اللاعبين على بداية الطريق نحو الاحتراف الكروي وفق أسس علمية حديثة.

من جانبه، أعرب رئيس مجلس إدارة أكاديمية تشامبيونز، رجب السراج، عن تطلعه لأن يشكل هذا المعسكر نقطة انطلاق حقيقية للمواهب الفلسطينية نحو آفاق عالمية، مؤكدًا أن فلسطين تزخر بطاقات رياضية واعدة تحتاج إلى الدعم وتهيئة الظروف المناسبة لإبرازها. وأضاف أن الأكاديمية تعمل على تنفيذ مشاريع دولية نوعية ستسهم في إحداث نقلة استراتيجية في قطاع كرة القدم.

وفتحت الأكاديمية باب التسجيل للفئات العمرية من 6 إلى 16 عامًا، ضمن أعداد محددة تتناسب مع القدرة الاستيعابية للمعسكر، على أن يتم تقسيم المشاركين إلى مجموعات بحسب الأعمار استعدادًا لانطلاق البرنامج التدريبي. كما يشارك في المعسكر عدد من الكوادر الفنية لتشامبيونز إلى جانب مدربي ريال مدريد، بهدف تبادل الخبرات وتعزيز الكفاءة التدريبية المحلية.

وسيحصل اللاعبون المشاركون على أولوية للمشاركة في بطولات الناشئين التي ينظمها نادي ريال مدريد مستقبلًا، إضافة إلى شهادة معتمدة من النادي الملكي بعد إتمام البرنامج التدريبي بنجاح.

يُذكر أن أكاديمية تشامبيونز تُعد من أكبر الأكاديميات الرياضية في فلسطين والوطن العربي، وقد حققت نقلة نوعية في تطوير رياضات الناشئين في قطاع غزة من خلال شراكات واتفاقيات دولية ومشاركات فاعلة في بطولات محلية ودولية، إلى جانب تتويجها بكأس أكاديميات غزة في مختلف الفئات العمرية.`,
    },
    image: cover('real-madrid-partnership'),
    gallery: gallery('real-madrid-partnership', 4),
    date: '2020-10-05',
    category: 'partnerships',
    author: 'Champions Academy',
  },
  {
    id: '2020-regional-sport-agreement',
    slug: 'regional-sport-agreement',
    title: {
      en: 'Champions Sign an Exclusive Deal with UAE\'s "Regional Sport", Opening the Door to European Professionalism',
      ar: 'تشامبيونز توقّع اتفاقية حصرية مع "ريجينال سبورت" الإماراتية وتفتح باب الاحتراف الأوروبي لأول مرة في فلسطين',
    },
    excerpt: {
      en: 'Champions become the exclusive agent of Regional Sport in Gaza and the West Bank, enabling player marketing in 15+ European countries.',
      ar: 'تشامبيونز تصبح الوكيل الحصري لشركة ريجينال في غزة والضفة، ما يتيح تسويق اللاعبين في أكثر من 15 دولة أوروبية.',
    },
    content: {
      en: `In a historic step for Palestinian sport, Champions Sports Academy announced — via a live broadcast from the United Arab Emirates — the signing of an exclusive agreement with Regional Sport, a company specialized in marketing players internationally and developing various sports.

Mr. Rajab Nasser Al-Sarraj, chairman of the board of Champions Academy and Club, affirmed the importance of this agreement for Palestinian and Arab sport, noting that Regional is one of the largest Arab and international companies in the field of marketing players and opening pathways to professionalism in Europe and several countries around the world. Al-Sarraj explained that, under this agreement, Champions Academy has become the exclusive and first agent for Regional in the Gaza Strip and the West Bank.

He noted that this partnership would enable — for the first time in Palestine — the marketing of local emerging players in more than 15 European countries and clubs, through specialized programs to be announced in a later press conference, including preparing and qualifying players according to international professional standards and showcasing their skills within a professional sports-marketing system that meets European requirements.

Al-Sarraj pointed out that the agreement would help open horizons of international professionalism for Palestinian sporting talents, especially in football, and would reflect positively on the Palestinian sports economy and on the clubs whose players turn professional. He also revealed work on launching advanced international training programs for players, coaches and scouts, to be announced soon.

For his part, Mr. Omar Al-Dhiyabi, chairman of Regional Sport, expressed his pride in partnering with Champions Academy, describing it as a distinguished sporting edifice and affirming that the agreement comes within Regional's strategy to support and develop Arab sport and strengthen the presence of Arab players in European leagues. He also expressed his great confidence in Champions' ability to make this partnership a success and achieve quality results on the ground.

Champions Academy is considered one of the largest sports academies in Palestine and the Arab region, and has achieved a qualitative leap in developing youth sports in the Gaza Strip through its continuous participation in local and international tournaments — the latest of which was its participation in the Donosti Cup international tournament in Spain.`,
      ar: `في خطوة تاريخية للرياضة الفلسطينية، أعلنت أكاديمية تشامبيونز الرياضية – عبر بث مباشر من دولة الإمارات العربية المتحدة – عن توقيع اتفاقية حصرية مع شركة ريجينال للرياضات الدولية، المتخصصة في تسويق اللاعبين دوليًا وتطوير مختلف الألعاب الرياضية.

وأكد الأستاذ رجب ناصر السراج، رئيس مجلس إدارة أكاديمية ونادي تشامبيونز، أهمية هذه الاتفاقية للرياضة الفلسطينية والعربية، مشيرًا إلى أن شركة ريجينال تُعد من كبرى الشركات العربية والعالمية في مجال تسويق اللاعبين وفتح مسارات احترافهم في أوروبا ودول عدة حول العالم. وأوضح السراج أن أكاديمية تشامبيونز أصبحت بموجب هذه الاتفاقية الوكيل الحصري والأول لشركة ريجينال في قطاع غزة والضفة الغربية.

وبيّن أن هذه الشراكة ستتيح – ولأول مرة في فلسطين – إمكانية تسويق اللاعبين الناشئين المحليين في أكثر من 15 دولة وناديًا أوروبيًا، من خلال برامج متخصصة سيتم الإعلان عنها في مؤتمر صحفي لاحق، تشمل إعداد اللاعبين وتأهيلهم وفق معايير احترافية دولية، وعرض مهاراتهم ضمن منظومة تسويق رياضي احترافية تواكب المتطلبات الأوروبية.

وأشار السراج إلى أن الاتفاقية ستسهم في فتح آفاق الاحتراف الدولي أمام المواهب الرياضية الفلسطينية، خاصة في كرة القدم، كما ستنعكس إيجابًا على الاقتصاد الرياضي الفلسطيني والأندية التي سيتم احتراف لاعبيها. كما كشف عن العمل على إطلاق برامج تدريبية دولية متقدمة للاعبين والمدربين والكشافين سيتم الإعلان عنها قريبًا.

من جانبه، عبّر السيد عمر الذيابي، رئيس مجلس إدارة شركة ريجينال للرياضات، عن فخره بالشراكة مع أكاديمية تشامبيونز، واصفًا إياها بالصرح الرياضي المتميز، مؤكدًا أن هذه الاتفاقية تأتي ضمن استراتيجية ريجينال لدعم وتطوير الرياضة العربية وتعزيز حضور اللاعبين العرب في الدوريات الأوروبية. كما أعرب عن ثقته الكبيرة في قدرة تشامبيونز على إنجاح هذه الشراكة وتحقيق نتائج نوعية على أرض الواقع.

يُذكر أن أكاديمية تشامبيونز تُعد من أكبر الأكاديميات الرياضية في فلسطين والمنطقة العربية، وقد حققت نقلة نوعية في تطوير رياضات الناشئين في قطاع غزة، من خلال مشاركاتها المتواصلة في البطولات المحلية والدولية، كان آخرها مشاركتها في بطولة دونستي الدولية في إسبانيا.`,
    },
    image: cover('regional-sport-agreement'),
    gallery: gallery('regional-sport-agreement', 4),
    date: '2020-08-20',
    category: 'partnerships',
    author: 'Champions Academy',
  },

  // ===================== 2019 =====================
  {
    id: '2019-talal-shiblaq-karate-gold',
    slug: 'talal-shiblaq-karate-gold',
    title: {
      en: 'Champions Star Talal Shiblaq Shines and Takes First Place',
      ar: 'طلال شبلاق بطل تشامبيونز يتألق ويحصد المركز الأول',
    },
    excerpt: {
      en: 'Captain Talal Shiblaq wins first place in Gaza and third in Palestine in karate after a three-year injury layoff.',
      ar: 'الكابتن طلال شبلاق يتوج بالمركز الأول على مستوى غزة والثالث على مستوى فلسطين بعد غياب ثلاث سنوات بسبب الإصابة.',
    },
    content: {
      en: `Champions Club's champions continued their brilliance on the sporting scene, after Captain Talal Emad Shiblaq was crowned first across the Gaza Strip and third across Palestine in the Palestine Karate Championship.

This achievement came in the "Kumite — fighting" competitions for the over-18 category, under 75 kg, within a tournament that brought together champions from the Gaza Strip and the West Bank in a strong competitive atmosphere.

This title carries special significance, as it comes after the champion Talal's return to competition following a three-year absence due to injury — confirming his determination and insistence on returning strongly to the podiums. Champions Club hopes this return will be the beginning of a new journey toward representing Palestine in international arenas.

A thousand congratulations to our champion, with our sincere wishes for continued success and accomplishment.`,
      ar: `واصل أبطال نادي تشامبيونز تألقهم على الساحة الرياضية، بعدما توّج الكابتن طلال عماد شبلاق بالمركز الأول على مستوى قطاع غزة، والمركز الثالث على مستوى فلسطين، في بطولة فلسطين للكراتيه.

وجاء هذا الإنجاز في منافسات "كوميتيه – القتال" لفئة فوق 18 عامًا، وزن أقل من 75 كغم، ضمن بطولة جمعت أبطال قطاع غزة والضفة الغربية في أجواء تنافسية قوية.

ويكتسب هذا التتويج أهمية خاصة، كونه يأتي بعد عودة البطل طلال إلى المنافسات عقب غياب دام ثلاث سنوات بسبب الإصابة، ليؤكد عزيمته وإصراره على العودة بقوة إلى منصات التتويج. ويأمل نادي تشامبيونز أن تكون هذه العودة بداية لمسيرة جديدة نحو تمثيل فلسطين في المحافل الدولية.

ألف مبارك لبطلنا، مع خالص التمنيات له بدوام التوفيق والنجاح.`,
    },
    image: cover('talal-shiblaq-karate-gold'),
    gallery: gallery('talal-shiblaq-karate-gold', 4),
    date: '2019-11-15',
    category: 'achievements',
    author: 'Champions Academy',
  },
  {
    id: '2019-first-schools-olympiad',
    slug: 'first-schools-olympiad',
    title: {
      en: 'Champions Launch Their First Schools Olympiad',
      ar: 'افتتاح أول أولمبياد لطلاب المدارس في تشامبيونز',
    },
    excerpt: {
      en: 'Champions open the first edition of the Private Schools Olympiad, with the torch lit by Olympic runner Nader Al-Masri.',
      ar: 'تشامبيونز تفتتح أولى نسخ أولمبياد المدارس الخاصة بإيقاد الشعلة على يد العداء الأولمبي نادر المصري.',
    },
    content: {
      en: `Champions Club witnessed the opening ceremony of the Champions Private Schools Olympiad, in the presence of the board of directors and the administrations of the participating schools, in a distinguished launch of the first edition of this sporting event that brings school students together in an enthusiastic, competitive atmosphere.

The opening ceremony featured the lighting of the Olympiad torch by Palestinian Olympic runner Nader Al-Masri, with the participation of Champions' runners, in a symbolic scene embodying the spirit of perseverance and sporting ambition.

Following the opening rituals, the events of the Olympiad's first day got under way, marking the start of a series of competitions and sporting activities aimed at strengthening sportsmanship and developing students' abilities in a positive competitive environment.`,
      ar: `شهد نادي تشامبيونز حفل افتتاح أولمبياد تشامبيونز للمدارس الخاصة، بحضور مجلس الإدارة وإدارات المدارس المشاركة، في انطلاقة مميزة لأول نسخة من هذا الحدث الرياضي الذي يجمع طلبة المدارس في أجواء تنافسية مفعمة بالحماس.

وتخلل حفل الافتتاح إيقاد شعلة الأولمبياد على يد العداء الأولمبي الفلسطيني نادر المصري، بمشاركة عدّائي تشامبيونز، في مشهد رمزي يجسد روح الإصرار والطموح الرياضي.

وعقب مراسم الافتتاح، انطلقت فعاليات اليوم الأول من الأولمبياد، إيذانًا ببدء سلسلة من المنافسات والأنشطة الرياضية التي تهدف إلى تعزيز الروح الرياضية وتنمية قدرات الطلبة في بيئة تنافسية إيجابية.`,
    },
    image: cover('first-schools-olympiad'),
    gallery: gallery('first-schools-olympiad', 4),
    date: '2019-09-20',
    category: 'events',
    author: 'Champions Academy',
  },
  {
    id: '2019-donosti-cup-spain',
    slug: 'donosti-cup-spain',
    title: {
      en: 'Champions Take Part in Spain\'s International Donosti Cup',
      ar: 'تشامبيونز يشارك في بطولة دونستي الإسبانية الدولية',
    },
    excerpt: {
      en: 'A Champions delegation of 20 players travels to San Sebastián for the Donosti Cup, one of the strongest international tournaments in its category.',
      ar: 'بعثة تشامبيونز بقوام 20 لاعبًا تتوجه إلى سان سباستيان للمشاركة في بطولة دونستي، ثاني أقوى بطولة دولية في فئتها.',
    },
    content: {
      en: `The Champions Club delegation arrived in the Spanish capital Madrid, before heading to the city of San Sebastián, to take part in the international Donosti Cup — considered the second-strongest international tournament in the world in its category.

The club had received an official invitation to take part in the tournament's competitions, a new achievement added to its record of external participations.

The delegation is headed by the chairman of the Champions Club board, Rajab Al-Sarraj, and includes delegation administrator Mahmoud Al-Khodari, team coaches Captain Ahmad Al-Daour and Captain Abdel-Aal Al-Attar, alongside the delegation's media officer Ahmad Skaik. The roster also includes 20 players born in 2006–2007, selected after a series of preliminary and intensive trials conducted by the club's technical committee to choose the final squad taking part in the tournament.

This participation comes within the agenda of external events that Champions Club is working on, as part of its ongoing efforts to represent the State of Palestine in international arenas and strengthen the presence of Palestinian talents on the world stage.`,
      ar: `وصلت بعثة نادي تشامبيونز إلى العاصمة الإسبانية مدريد، قبل التوجه إلى مدينة سان سباستيان، للمشاركة في بطولة دونستي الدولية، والتي تُعد ثاني أقوى بطولة دولية على مستوى العالم في فئتها.

وكان النادي قد تلقى دعوة رسمية للمشاركة في منافسات البطولة، في إنجاز جديد يُضاف إلى سجل مشاركاته الخارجية.

ويترأس البعثة رئيس مجلس إدارة نادي تشامبيونز رجب السراج، ويضم الوفد إداري البعثة محمود الخضري، ومدربي الفريق الكابتن أحمد الداعور والكابتن عبد العال العطار، إلى جانب إعلامي البعثة أحمد سكيك. كما تضم القائمة 20 لاعبًا من مواليد 2006–2007، تم اختيارهم بعد سلسلة من الاختبارات الأولية والمكثفة التي أجرتها اللجنة الفنية بالنادي لاختيار التشكيلة النهائية المشاركة في البطولة.

وتأتي هذه المشاركة ضمن أجندة الفعاليات الخارجية التي يعمل عليها نادي تشامبيونز، في إطار جهوده المستمرة لتمثيل دولة فلسطين في المحافل الدولية وتعزيز حضور المواهب الفلسطينية على الساحة العالمية.`,
    },
    image: cover('donosti-cup-spain'),
    gallery: gallery('donosti-cup-spain', 4),
    date: '2019-07-12',
    category: 'international',
    author: 'Champions Academy',
  },
  {
    id: '2019-cancer-football-academy',
    slug: 'cancer-football-academy',
    title: {
      en: "The World's First Football Academy for Child Cancer Patients",
      ar: 'أول أكاديمية لكرة القدم لمرضى السرطان في العالم',
    },
    excerpt: {
      en: 'Champions opens the first-of-its-kind football academy for children with cancer in Gaza, where they ease their pain with football.',
      ar: 'تشامبيونز تفتتح أول أكاديمية كرة قدم لأطفال السرطان في غزة، حيث يعالجون أوجاعهم بكرة القدم.',
    },
    content: {
      en: `Champions Academy launched a humanitarian sporting initiative that is the first of its kind in the world: a football academy dedicated to children battling cancer in the Gaza Strip — a space where the young ones ease their pain and find joy through football, far from the harshness of treatment and illness.

The initiative aims to bring hope and happiness to the children and to support them psychologically and physically through sport, within an environment designed to suit their conditions and capabilities, and under the care of specialized training and medical staff.

The initiative drew wide local and international media attention, with reports highlighting Champions' pioneering humanitarian role in turning sport into a means of healing and support for the children of Gaza.

Read more (Arabic coverage):
• Mugtama: https://mugtama.com/articles/افتتاح_أول_أكاديمية_رياضية_أطفال_السرطان_بغزة_يعالجون_أوجاعهم_بكرة_القدم
• Al Jazeera: https://www.aljazeera.net/misc/2019/2/7/غزة-أطفال-سرطان-كرة-القدم`,
      ar: `أطلقت أكاديمية تشامبيونز مبادرة رياضية إنسانية هي الأولى من نوعها في العالم: أكاديمية لكرة القدم مخصصة للأطفال المصابين بمرض السرطان في قطاع غزة، حيث يعالج الصغار أوجاعهم ويجدون الفرح من خلال كرة القدم، بعيدًا عن قسوة العلاج والمرض.

وتهدف المبادرة إلى إدخال الأمل والسعادة إلى نفوس الأطفال، ودعمهم نفسيًا وبدنيًا من خلال الرياضة، في بيئة مهيأة تناسب ظروفهم وقدراتهم، وتحت رعاية طواقم تدريبية وطبية متخصصة.

ولاقت المبادرة اهتمامًا إعلاميًا محليًا ودوليًا واسعًا، حيث سلطت التقارير الضوء على الدور الإنساني الريادي لتشامبيونز في تحويل الرياضة إلى وسيلة للعلاج والدعم لأطفال غزة.

للمزيد (تغطيات صحفية):
• مجتمع: https://mugtama.com/articles/افتتاح_أول_أكاديمية_رياضية_أطفال_السرطان_بغزة_يعالجون_أوجاعهم_بكرة_القدم
• الجزيرة: https://www.aljazeera.net/misc/2019/2/7/غزة-أطفال-سرطان-كرة-القدم`,
    },
    image: cover('cancer-football-academy'),
    gallery: gallery('cancer-football-academy', 7),
    date: '2019-02-07',
    category: 'community',
    author: 'Champions Academy',
  },

  // ===================== 2018 =====================
  {
    id: '2018-multiple-academies-gaza',
    slug: 'multiple-academies-gaza',
    title: {
      en: 'Multiple Sports Academies Open at the Family Club in Gaza City',
      ar: 'افتتاح أكاديميات رياضية متعددة في النادي العائلي بمدينة غزة',
    },
    excerpt: {
      en: 'Champions opens specialized academies in basketball, table tennis, karate and swimming for both genders.',
      ar: 'تشامبيونز تفتتح أكاديميات متخصصة في كرة السلة وتنس الطاولة والكراتيه والسباحة لكلا الجنسين.',
    },
    content: {
      en: `Champions Academy announced the opening of a group of specialized sports academies inside the family club in Gaza City at the end of the year, as part of its plan to broaden the base of sporting participation and enhance the diversity of activities offered to the community.

The new academies include training programs for both genders in basketball, table tennis, karate and swimming, supervised by qualified coaching staff and according to modern scientific foundations that ensure skill development and the honing of talents.

This step reaffirms Champions' vision of providing an integrated sporting environment that supports the various age groups and contributes to building an aware and capable sporting generation.`,
      ar: `أعلنت أكاديمية تشامبيونز عن افتتاح مجموعة من الأكاديميات الرياضية المتخصصة داخل النادي العائلي في مدينة غزة، وذلك مع نهاية العام، في إطار خطتها لتوسيع قاعدة المشاركة الرياضية وتعزيز تنوع الأنشطة المقدمة للمجتمع.

وتشمل الأكاديميات الجديدة برامج تدريبية لكلا الجنسين في رياضات كرة السلة، وتنس الطاولة، والكراتيه، والسباحة، بإشراف طواقم تدريبية مؤهلة ووفق أسس علمية حديثة تضمن تطوير المهارات وصقل المواهب.

وتأتي هذه الخطوة تأكيدًا على رؤية تشامبيونز في توفير بيئة رياضية متكاملة تدعم مختلف الفئات العمرية، وتسهم في بناء جيل رياضي واعٍ ومتمكن.`,
    },
    image: cover('multiple-academies-gaza'),
    gallery: gallery('multiple-academies-gaza', 16),
    date: '2018-12-20',
    category: 'facilities',
    author: 'Champions Academy',
  },
  {
    id: '2018-first-family-club-palestine',
    slug: 'first-family-club-palestine',
    title: {
      en: "Champions Opens Palestine's First Fully-Integrated Family Club",
      ar: 'افتتاح أول نادٍ عائلي متكامل في فلسطين',
    },
    excerpt: {
      en: 'Champions launches the first all-in-one family sports club in Palestine, bringing the whole family together under one roof.',
      ar: 'نادي تشامبيونز يفتتح أول نادٍ عائلي متكامل في فلسطين يجمع أفراد العائلة تحت سقف واحد.',
    },
    content: {
      en: `Champions Club announced the opening of the first fully-integrated family club in Palestine — a pioneering step aimed at meeting the needs of the Gaza community and providing a sports and recreation space that brings family members together under one roof.

The club introduces a new concept to the world of sports clubs, offering a variety of sports suited to different age groups and ensuring a complete experience that serves every member of the family, in a modern, safe environment that promotes physical activity and a spirit of community participation.

This opening reaffirms Champions' commitment to developing the sports sector in Palestine and creating distinctive initiatives that contribute to building a healthier, more cohesive society.`,
      ar: `أعلن نادي تشامبيونز عن افتتاح أول نادٍ عائلي متكامل في فلسطين، في خطوة ريادية تهدف إلى تلبية احتياجات المجتمع الغزي وتوفير مساحة رياضية وترفيهية تجمع أفراد العائلة تحت سقف واحد.

ويقدم النادي مفهومًا جديدًا في عالم الأندية الرياضية، حيث يتيح ممارسة رياضات متعددة تناسب مختلف الفئات العمرية، بما يضمن تجربة متكاملة تخدم جميع أفراد الأسرة، في بيئة عصرية وآمنة تعزز النشاط البدني وروح المشاركة المجتمعية.

ويأتي هذا الافتتاح تأكيدًا على التزام تشامبيونز بتطوير القطاع الرياضي في فلسطين، وابتكار مبادرات نوعية تسهم في بناء مجتمع أكثر صحة وتماسكًا.`,
    },
    image: cover('first-family-club-palestine'),
    gallery: gallery('first-family-club-palestine', 6),
    date: '2018-06-15',
    category: 'facilities',
    author: 'Champions Academy',
  },
];
